// @ts-nocheck
import * as THREE from 'three'
import {
  generateDataTexture,
  resizeVisibleNodesTexture,
} from '@/plugins/pointCloud/utils/geometry-utils'
import { ElevationGradientRepeat, PointShape, PointSizeType, TreeType } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import { ClassificationScheme } from './ClassificationScheme.js'
import { Gradients } from './Gradients.js'
import { Shaders } from './shaders/shaders.js'

const log = getLoggerManager().getLogger('pointcloud')
//
// how to calculate the radius of a projected sphere in screen space
// http://stackoverflow.com/questions/21648630/radius-of-projected-sphere-in-screen-space
// http://stackoverflow.com/questions/3717226/radius-of-projected-sphere
//

/**
 * 点云渲染主材质，管理 shader 所需的所有 uniform、纹理与动态 defines。
 */
export class PointCloudMaterial extends THREE.RawShaderMaterial {
  /** 可见节点数据纹理，描述八叉树层级结构供 shader 采样 */
  visibleNodesTexture
  _pointSizeType
  _shape
  _useClipBox
  /** 裁切盒数据列表，由 setClipBoxes 写入 */
  clipBoxes
  /** 裁切多边形数据列表，由 setClipPolygons 写入 */
  clipPolygons
  _weighted
  _gradient
  /** 渐变色映射纹理（懒生成） */
  gradientTexture
  _matcap
  /** Matcap 纹理（懒生成） */
  matcapTexture
  _treeType
  _useEDL
  /** 属性到取值范围的映射，供归一化使用 */
  ranges
  _activeAttributeName
  _defaultElevationRangeChanged
  _defaultIntensityRangeChanged
  /** 分类 LUT 数据纹理 */
  classificationTexture
  /** 点云属性描述引用 */
  attributes
  /** 当前分类方案快照 */
  classification
  _hiddenListeners
  _listeners

  constructor(
    parameters: {
      size?: number
      minSize?: number
      maxSize?: number
      treeType?: number
    } = {}
  ) {
    super()

    this.glslVersion = THREE.GLSL3

    this.visibleNodesTexture = generateDataTexture(2048, 1, new THREE.Color(0xffffff))
    this.visibleNodesTexture.minFilter = THREE.NearestFilter
    this.visibleNodesTexture.magFilter = THREE.NearestFilter

    const getValid = (a, b) => {
      if (a !== undefined) {
        return a
      } else {
        return b
      }
    }

    const pointSize = getValid(parameters.size, 1.0)
    const minSize = getValid(parameters.minSize, 1.0)
    const maxSize = getValid(parameters.maxSize, 20.0)
    const treeType = getValid(parameters.treeType, TreeType.OCTREE)

    this._pointSizeType = PointSizeType.FIXED
    this._shape = PointShape.SQUARE
    this._useClipBox = false
    this.clipBoxes = []
    this.clipPolygons = []
    this._weighted = false
    this._gradient = Gradients.SPECTRAL
    this.gradientTexture = PointCloudMaterial.generateGradientTexture(this._gradient)
    this._matcap = null
    this.matcapTexture = null
    this.lights = false
    this.fog = false
    this._treeType = treeType
    this._useEDL = false
    this.defines = new Map()

    this.ranges = new Map()

    this._activeAttributeName = null

    this._defaultIntensityRangeChanged = false
    this._defaultElevationRangeChanged = false

    {
      const [width, height] = [256, 1]
      const data = new Uint8Array(width * 4)
      const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat)
      texture.minFilter = THREE.NearestFilter
      texture.magFilter = THREE.NearestFilter
      // LUT 保存的是供点云 Raw Shader 直接输出的 sRGB 编码值，不由纹理采样器解码。
      texture.colorSpace = THREE.NoColorSpace
      texture.needsUpdate = true

      this.classificationTexture = texture
    }

    this.attributes = {
      position: { type: 'fv', value: [] },
      color: { type: 'fv', value: [] },
      normal: { type: 'fv', value: [] },
      intensity: { type: 'f', value: [] },
      classification: { type: 'f', value: [] },
      returnNumber: { type: 'f', value: [] },
      numberOfReturns: { type: 'f', value: [] },
      pointSourceID: { type: 'f', value: [] },
      indices: { type: 'fv', value: [] },
    }

    this.uniforms = {
      level: { type: 'f', value: 0.0 },
      vnStart: { type: 'f', value: 0.0 },
      spacing: { type: 'f', value: 1.0 },
      blendHardness: { type: 'f', value: 2.0 },
      blendDepthSupplement: { type: 'f', value: 0.0 },
      fov: { type: 'f', value: 1.0 },
      screenWidth: { type: 'f', value: 1.0 },
      screenHeight: { type: 'f', value: 1.0 },
      near: { type: 'f', value: 0.1 },
      far: { type: 'f', value: 1.0 },
      uPickMinDistanceEnabled: { type: 'b', value: false },
      uPickMinDistance: { type: 'f', value: 0 },
      uPickRayOriginView: { type: 'v3', value: new THREE.Vector3() },
      uPickRayDirectionView: { type: 'v3', value: new THREE.Vector3(0, 0, -1) },
      uColor: { type: 'c', value: new THREE.Color(0xffffff) },
      uOpacity: { type: 'f', value: 1.0 },
      size: { type: 'f', value: pointSize },
      minSize: { type: 'f', value: minSize },
      maxSize: { type: 'f', value: maxSize },
      octreeSize: { type: 'f', value: 0 },
      bbSize: { type: 'fv', value: [0, 0, 0] },
      elevationRange: { type: '2fv', value: [0, 0] },

      clipBoxCount: { type: 'f', value: 0 },
      //clipSphereCount:	{ type: "f", value: 0 },
      clipPolygonCount: { type: 'i', value: 0 },
      clipBoxes: { type: 'Matrix4fv', value: [] },
      //clipSpheres:		{ type: "Matrix4fv", value: [] },
      clipPolygons: { type: '3fv', value: [] },
      clipPolygonVCount: { type: 'iv', value: [] },
      clipPolygonVP: { type: 'Matrix4fv', value: [] },

      visibleNodes: { type: 't', value: this.visibleNodesTexture },
      pcIndex: { type: 'f', value: 0 },
      gradient: { type: 't', value: this.gradientTexture },
      classificationLUT: { type: 't', value: this.classificationTexture },
      uHQDepthMap: { type: 't', value: null },
      toModel: { type: 'Matrix4f', value: [] },
      diffuse: { type: 'fv', value: [1, 1, 1] },
      transition: { type: 'f', value: 0.5 },

      intensityRange: { type: 'fv', value: [Infinity, -Infinity] },
      activeSamplingRange: { type: 'fv', value: [Infinity, -Infinity] },

      intensity_gbc: { type: 'fv', value: [1, 0, 0] },
      uRGB_gbc: { type: 'fv', value: [1, 0, 0] },
      // intensityGamma:		{ type: "f", value: 1 },
      // intensityContrast:	{ type: "f", value: 0 },
      // intensityBrightness:{ type: "f", value: 0 },
      // rgbGamma:			{ type: "f", value: 1 },
      // rgbContrast:		{ type: "f", value: 0 },
      // rgbBrightness:		{ type: "f", value: 0 },
      wRGB: { type: 'f', value: 1 },
      wIntensity: { type: 'f', value: 0 },
      wElevation: { type: 'f', value: 0 },
      wClassification: { type: 'f', value: 0 },
      wReturnNumber: { type: 'f', value: 0 },
      wSourceID: { type: 'f', value: 0 },
      useOrthographicCamera: { type: 'b', value: false },
      elevationGradientRepeat: {
        type: 'i',
        value: ElevationGradientRepeat.CLAMP,
      },
      clipTask: { type: 'i', value: 1 },
      clipMethod: { type: 'i', value: 1 },
      uShadowColor: { type: '3fv', value: [0, 0, 0] },

      uExtraScale: { type: 'f', value: 1 },
      uExtraOffset: { type: 'f', value: 0 },
      uExtraRange: { type: '2fv', value: [0, 1] },
      uExtraGammaBrightContr: { type: '3fv', value: [1, 0, 0] },

      uFilterReturnNumberRange: { type: 'fv', value: [0, 7] },
      uFilterNumberOfReturnsRange: { type: 'fv', value: [0, 7] },
      // Null means "no GPS time clipping"; PotreeRenderer will map this to the
      // full normalized [0, 1] range once metadata is available.
      uFilterGPSTimeClipRange: { type: 'fv', value: null },
      uFilterPointSourceIDClipRange: { type: 'fv', value: [0, 65535] },
      matcapTextureUniform: { type: 't', value: this.matcapTexture },
      backfaceCulling: { type: 'b', value: false },
      uEnableZClip: { type: 'b', value: false },
      uZClipRange: { type: 'fv', value: [0, 0] },
    }

    this.classification = ClassificationScheme.DEFAULT
    this.recomputeClassification()

    this.defaultAttributeValues.normal = [0, 0, 0]
    this.defaultAttributeValues.classification = [0, 0, 0]
    this.defaultAttributeValues.indices = [0, 0, 0, 0]

    this.vertexShader = Shaders['pointcloud.vs']
    this.fragmentShader = Shaders['pointcloud.fs']

    // this.vertexColors = THREE.VertexColors;

    this.updateShaderSource()
  }

  /**
   * 设置或移除 shader 宏定义，变化时自动触发 shader 重编译。
   *
   * @param key - 宏名，如 'use_edl'
   * @param value - 宏值；传 null/undefined 等同 removeDefine
   */
  setDefine(key, value) {
    if (value !== undefined && value !== null) {
      if (this.defines.get(key) !== value) {
        this.defines.set(key, value)
        this.updateShaderSource()
      }
    } else {
      this.removeDefine(key)
    }
  }

  /**
   * 移除 shader 宏定义。
   *
   * @param key - 宏名
   */
  removeDefine(key) {
    this.defines.delete(key)
  }

  /**
   * 更新 visibleNodesTexture 到足够大小。
   *
   * @param requiredNodeCount - 所需节点数量，≥0
   * @param maxTextureSize - 设备最大纹理尺寸，来自 renderer.capabilities.maxTextureSize
   * @returns 更新成功返回 true；超限或失败时返回 false，业务层应跳过本帧
   */
  updateVisibleNodesTexture(requiredNodeCount: number, maxTextureSize: number): boolean {
    const currentCapacity =
      this.visibleNodesTexture.image.width * this.visibleNodesTexture.image.height

    if (requiredNodeCount <= currentCapacity) {
      return true
    }

    const result = resizeVisibleNodesTexture(requiredNodeCount, maxTextureSize)

    if (!result.success) {
      log.warn(`[PointCloudMaterial] Texture resize failed: ${result.message}`)
      return false // 告诉业务层跳过本次渲染
    }

    const newTexture = result.texture
    const oldTexture = this.visibleNodesTexture

    // 替换引用
    this.visibleNodesTexture = newTexture

    // 更新材质 uniforms
    this.uniforms.visibleNodes.value = newTexture
    // this.visibleNodesTexture.needsUpdate = true

    // 销毁旧纹理
    if (oldTexture) {
      oldTexture.dispose()
    }

    return true
  }

  /** 根据当前状态重新构造 shader 源码与混合模式。 */
  updateShaderSource() {
    let vs = Shaders['pointcloud.vs'].trim()
    let fs = Shaders['pointcloud.fs'].trim()
    const definesString = this.getDefines()

    // 移除 shader 源码中的 #version 指令（由 glslVersion 属性控制）
    const versionString = '#version 300 es'
    vs = vs.replace(versionString, '').trim()
    fs = fs.replace(versionString, '').trim()

    // defines 放在 shader 源码最前面
    this.vertexShader = `${definesString}\n${vs}`
    this.fragmentShader = `${definesString}\n${fs}`

    if (this.opacity === 1.0) {
      this.blending = THREE.NoBlending
      this.transparent = false
      this.depthTest = true
      this.depthWrite = true
      this.depthFunc = THREE.LessEqualDepth
    } else if (this.opacity < 1.0 && !this.useEDL) {
      this.blending = THREE.NormalBlending
      this.transparent = true
      this.depthTest = true
      this.depthWrite = false
      this.depthFunc = THREE.LessEqualDepth
    }

    if (this.weighted) {
      this.blending = THREE.AdditiveBlending
      this.transparent = true
      this.depthTest = true
      this.depthWrite = false
    }

    this.needsUpdate = true
  }

  /**
   * 根据当前材质状态生成 shader 宏定义串。
   *
   * @returns 含若干 `#define` 的 GLSL 片段
   */
  getDefines() {
    const defines: string[] = []

    if (this.pointSizeType === PointSizeType.FIXED) {
      defines.push('#define fixed_point_size')
    } else if (this.pointSizeType === PointSizeType.ATTENUATED) {
      defines.push('#define attenuated_point_size')
    } else if (this.pointSizeType === PointSizeType.ADAPTIVE) {
      defines.push('#define adaptive_point_size')
    }

    if (this.shape === PointShape.SQUARE) {
      defines.push('#define square_point_shape')
    } else if (this.shape === PointShape.CIRCLE) {
      defines.push('#define circle_point_shape')
    } else if (this.shape === PointShape.PARABOLOID) {
      defines.push('#define paraboloid_point_shape')
    }

    if (this._useEDL) {
      defines.push('#define use_edl')
    }

    if (this.activeAttributeName) {
      const attributeName = this.activeAttributeName.replace(/[^a-zA-Z0-9]/g, '_')

      defines.push(`#define color_type_${attributeName}`)
    }

    if (this._treeType === TreeType.OCTREE) {
      defines.push('#define tree_type_octree')
    } else if (this._treeType === TreeType.KDTREE) {
      defines.push('#define tree_type_kdtree')
    }

    if (this.weighted) {
      defines.push('#define weighted_splats')
    }

    for (const [, value] of this.defines) {
      defines.push(value)
    }

    return defines.join('\n')
  }

  /**
   * 批量写入裁切盒数据到 uniforms。
   *
   * @param clipBoxes - 裁切盒列表，每项含 inverse 矩阵；falsy 时忽略
   */
  setClipBoxes(clipBoxes) {
    if (!clipBoxes) {
      return
    }
    const doUpdate =
      this.clipBoxes.length !== clipBoxes.length &&
      (clipBoxes.length === 0 || this.clipBoxes.length === 0)

    this.uniforms.clipBoxCount.value = this.clipBoxes.length
    this.clipBoxes = clipBoxes

    if (doUpdate) {
      this.updateShaderSource()
    }

    this.uniforms.clipBoxes.value = new Float32Array(this.clipBoxes.length * 16)

    for (let i = 0; i < this.clipBoxes.length; i++) {
      const box = clipBoxes[i]

      this.uniforms.clipBoxes.value.set(box.inverse.elements, 16 * i)
    }

    for (let i = 0; i < this.uniforms.clipBoxes.value.length; i++) {
      if (Number.isNaN(this.uniforms.clipBoxes.value[i])) {
        this.uniforms.clipBoxes.value[i] = Infinity
      }
    }
  }

  /**
   * 批量写入裁切多边形数据并按需触发 shader 重编译。
   *
   * @param clipPolygons - 裁切多边形列表；falsy 时忽略
   * @param maxPolygonVertices - 多边形最大顶点数，用于 shader 常量（预留）
   */
  setClipPolygons(clipPolygons, _maxPolygonVertices) {
    if (!clipPolygons) {
      return
    }

    const previousCount = this.clipPolygons.length
    this.clipPolygons = clipPolygons

    const doUpdate = previousCount !== clipPolygons.length

    if (doUpdate) {
      this.updateShaderSource()
    }
  }

  /** 高程/强度渐变色数组，设置时自动重建 gradientTexture */
  get gradient() {
    return this._gradient
  }

  set gradient(value) {
    if (this._gradient !== value) {
      this._gradient = value
      this.gradientTexture = PointCloudMaterial.generateGradientTexture(this._gradient)
      this.uniforms.gradient.value = this.gradientTexture
    }
  }

  /** Matcap 纹理 URL；null 表示不启用 */
  get matcap() {
    return this._matcap
  }

  /** 设置 matcap 纹理，传入完整 URL（可通过 resolveMatcapUrl 生成） */
  set matcap(value) {
    if (this._matcap !== value) {
      this._matcap = value
      this.matcapTexture = value ? PointCloudMaterial.generateMatcapTexture(value) : null
      this.uniforms.matcapTextureUniform.value = this.matcapTexture
    }
  }
  /** 是否按正交相机模式计算点大小 */
  get useOrthographicCamera() {
    return this.uniforms.useOrthographicCamera.value
  }

  set useOrthographicCamera(value) {
    if (this.uniforms.useOrthographicCamera.value !== value) {
      this.uniforms.useOrthographicCamera.value = value
    }
  }
  /** 是否启用背面剔除，赋值时派发 backface_changed 事件 */
  get backfaceCulling() {
    return this.uniforms.backfaceCulling.value
  }

  set backfaceCulling(value) {
    if (this.uniforms.backfaceCulling.value !== value) {
      this.uniforms.backfaceCulling.value = value
      this.dispatchEvent({ type: 'backface_changed', target: this })
    }
  }

  /** 根据当前 classification 方案重建分类 LUT 纹理，数据变化时派发事件。 */
  recomputeClassification() {
    const classification = this.classification
    const data = this.classificationTexture.image.data

    const width = 256
    const black = [1, 1, 1, 1]

    let valuesChanged = false

    for (let i = 0; i < width; i++) {
      let color: number[]
      let visible = true

      if (classification[i]) {
        color = classification[i].color
        visible = classification[i].visible
      } else if (classification[i % 32]) {
        color = classification[i % 32].color
        visible = classification[i % 32].visible
      } else if (classification.DEFAULT) {
        color = classification.DEFAULT.color
        visible = classification.DEFAULT.visible
      } else {
        color = black
      }

      const r = parseInt((255 * color[0]) as any, 10)
      const g = parseInt((255 * color[1]) as any, 10)
      const b = parseInt((255 * color[2]) as any, 10)
      const a = visible ? parseInt((255 * color[3]) as any, 10) : 0

      if (data[4 * i + 0] !== r) {
        data[4 * i + 0] = r
        valuesChanged = true
      }

      if (data[4 * i + 1] !== g) {
        data[4 * i + 1] = g
        valuesChanged = true
      }

      if (data[4 * i + 2] !== b) {
        data[4 * i + 2] = b
        valuesChanged = true
      }

      if (data[4 * i + 3] !== a) {
        data[4 * i + 3] = a
        valuesChanged = true
      }
    }

    if (valuesChanged) {
      this.classificationTexture.needsUpdate = true

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 替换分类方案并立即重建 classification LUT。 */
  setClassificationScheme(classification) {
    this.classification = classification
    this.recomputeClassification()
  }

  /** 八叉树点间距，单位米 */
  get spacing() {
    return this.uniforms.spacing.value
  }

  set spacing(value) {
    if (this.uniforms.spacing.value !== value) {
      this.uniforms.spacing.value = value
    }
  }

  /** 是否启用裁切盒，赋值时触发 shader 重编译 */
  get useClipBox() {
    return this._useClipBox
  }

  set useClipBox(value) {
    if (this._useClipBox !== value) {
      this._useClipBox = value
      this.updateShaderSource()
    }
  }

  /** 裁切任务类型，如 HIGHLIGHT/SHOW_INSIDE */
  get clipTask() {
    return this.uniforms.clipTask.value
  }

  set clipTask(mode) {
    this.uniforms.clipTask.value = mode
  }

  /** 高程渐变重复模式 */
  get elevationGradientRepeat() {
    return this.uniforms.elevationGradientRepeat.value
  }

  set elevationGradientRepeat(mode) {
    this.uniforms.elevationGradientRepeat.value = mode
  }

  /** 裁切方法，如 INSIDE_ANY/INSIDE_ALL */
  get clipMethod() {
    return this.uniforms.clipMethod.value
  }

  set clipMethod(mode) {
    this.uniforms.clipMethod.value = mode
  }

  /** 是否启用加权溅射模式，开启后改用 AdditiveBlending */
  get weighted() {
    return this._weighted
  }

  set weighted(value) {
    if (this._weighted !== value) {
      this._weighted = value
      this.updateShaderSource()
    }
  }

  /** 点大小上限 */
  get maxSize(): number {
    return this.uniforms.maxSize.value
  }

  set maxSize(value: number) {
    this.uniforms.maxSize.value = value
  }

  /** 相机垂直 FOV（弧度） */
  get fov() {
    return this.uniforms.fov.value
  }

  set fov(value) {
    if (this.uniforms.fov.value !== value) {
      this.uniforms.fov.value = value
      // this.updateShaderSource();
    }
  }

  /** 屏幕宽度，像素 */
  get screenWidth() {
    return this.uniforms.screenWidth.value
  }

  set screenWidth(value) {
    if (this.uniforms.screenWidth.value !== value) {
      this.uniforms.screenWidth.value = value
      // this.updateShaderSource();
    }
  }

  /** 屏幕高度，像素 */
  get screenHeight() {
    return this.uniforms.screenHeight.value
  }

  set screenHeight(value) {
    if (this.uniforms.screenHeight.value !== value) {
      this.uniforms.screenHeight.value = value
      // this.updateShaderSource();
    }
  }

  /** 相机近裁剪面距离 */
  get near() {
    return this.uniforms.near.value
  }

  set near(value) {
    if (this.uniforms.near.value !== value) {
      this.uniforms.near.value = value
    }
  }

  /** 相机远裁剪面距离 */
  get far() {
    return this.uniforms.far.value
  }

  set far(value) {
    if (this.uniforms.far.value !== value) {
      this.uniforms.far.value = value
    }
  }

  /** 点云整体不透明度，0-1；赋值时派发 opacity_changed 事件 */
  // @ts-expect-error
  get opacity() {
    return this.uniforms.uOpacity.value
  }

  set opacity(value) {
    if (this.uniforms?.uOpacity) {
      if (this.uniforms.uOpacity.value !== value) {
        this.uniforms.uOpacity.value = value
        this.updateShaderSource()
        this.dispatchEvent({
          type: 'opacity_changed',
          target: this,
        })
        this.dispatchEvent({
          type: 'material_property_changed',
          target: this,
        })
      }
    }
  }

  /** 当前激活的着色属性名，如 'rgba'、'elevation'、'intensity' */
  get activeAttributeName() {
    return this._activeAttributeName
  }

  set activeAttributeName(value) {
    if (this._activeAttributeName !== value) {
      this._activeAttributeName = value

      // 切换活动属性时更新
      if (value === 'elevation') {
        this.activeSamplingRange = this.elevationRange
      } else if (value?.includes('intensity')) {
        this.activeSamplingRange = this.intensityRange
      } else if (value === 'gps_time') {
        this.activeSamplingRange = [0, 1]
      }

      this.updateShaderSource()
      this.dispatchEvent({
        type: 'active_attribute_changed',
        target: this,
      })

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 点大小计算模式（FIXED/ATTENUATED/ADAPTIVE），赋值时触发 shader 重编译 */
  get pointSizeType() {
    return this._pointSizeType
  }

  set pointSizeType(value) {
    if (this._pointSizeType !== value) {
      this._pointSizeType = value
      this.updateShaderSource()
      this.dispatchEvent({
        type: 'point_size_type_changed',
        target: this,
      })
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 是否启用 EDL 深度增强输出，赋值时触发 shader 重编译 */
  get useEDL() {
    return this._useEDL
  }

  set useEDL(value) {
    if (this._useEDL !== value) {
      this._useEDL = value
      this.updateShaderSource()
    }
  }

  /** 单色模式基础色，赋值时派发 color_changed 事件 */
  get color() {
    return this.uniforms.uColor.value
  }

  set color(value) {
    if (!this.uniforms.uColor.value.equals(value)) {
      this.uniforms.uColor.value.copy(value)

      this.dispatchEvent({
        type: 'color_changed',
        target: this,
      })
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 点绘制形状（SQUARE/CIRCLE/PARABOLOID 等） */
  get shape() {
    return this._shape
  }

  set shape(value) {
    if (this._shape !== value) {
      this._shape = value
      this.updateShaderSource()
      this.dispatchEvent({ type: 'point_shape_changed', target: this })
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 空间索引树类型（OCTREE/KDTREE） */
  get treeType() {
    return this._treeType
  }

  set treeType(value) {
    if (this._treeType !== value) {
      this._treeType = value
      this.updateShaderSource()
    }
  }

  /** 八叉树包围盒尺寸向量 */
  get bbSize() {
    return this.uniforms.bbSize.value
  }

  set bbSize(value) {
    this.uniforms.bbSize.value = value
  }

  /** 点基础大小，单位像素或世界单位视 pointSizeType 而定 */
  get size() {
    return this.uniforms.size.value
  }

  set size(value) {
    if (this.uniforms.size.value !== value) {
      this.uniforms.size.value = value

      this.dispatchEvent({
        type: 'point_size_changed',
        target: this,
      })
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 点大小下限 */
  get minSize() {
    return this.uniforms.minSize.value
  }

  set minSize(value) {
    if (this.uniforms.minSize.value !== value) {
      this.uniforms.minSize.value = value

      this.dispatchEvent({
        type: 'point_size_changed',
        target: this,
      })
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 高程归一化范围 [min, max] */
  get elevationRange() {
    return this.uniforms.elevationRange.value
  }

  set elevationRange(value) {
    const changed =
      this.uniforms.elevationRange.value[0] !== value[0] ||
      this.uniforms.elevationRange.value[1] !== value[1]

    if (changed) {
      this.uniforms.elevationRange.value = value

      this._defaultElevationRangeChanged = true

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /**
   * 实际采样坐标点分布范围
   */
  get activeSamplingRange() {
    return this.uniforms.activeSamplingRange.value
  }
  set activeSamplingRange(value) {
    const changed =
      this.uniforms.activeSamplingRange.value[0] !== value[0] ||
      this.uniforms.activeSamplingRange.value[1] !== value[1]

    if (changed) {
      this.uniforms.activeSamplingRange.value[0] = value[0]
      this.uniforms.activeSamplingRange.value[1] = value[1]

      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 高程范围下限，等价于 elevationRange[0] */
  get heightMin() {
    return this.uniforms.elevationRange.value[0]
  }

  set heightMin(value) {
    this.elevationRange = [value, this.elevationRange[1]]
  }

  /** 高程范围上限，等价于 elevationRange[1] */
  get heightMax() {
    return this.uniforms.elevationRange.value[1]
  }

  set heightMax(value) {
    this.elevationRange = [this.elevationRange[0], value]
  }

  /** 多属性过渡混合系数，0-1 */
  get transition() {
    return this.uniforms.transition.value
  }

  set transition(value) {
    this.uniforms.transition.value = value
  }

  /** 强度归一化范围 [min, max] */
  get intensityRange() {
    return this.uniforms.intensityRange.value
  }

  set intensityRange(value) {
    if (!(Array.isArray(value) && value.length === 2)) {
      return
    }

    if (
      value[0] === this.uniforms.intensityRange.value[0] &&
      value[1] === this.uniforms.intensityRange.value[1]
    ) {
      return
    }

    this.uniforms.intensityRange.value = value

    this._defaultIntensityRangeChanged = true

    this.dispatchEvent({
      type: 'material_property_changed',
      target: this,
    })
  }

  /** 强度 gamma 校正系数 */
  get intensityGamma() {
    return this.uniforms.intensity_gbc.value[0]
  }

  set intensityGamma(value) {
    if (this.uniforms.intensity_gbc.value[0] !== value) {
      this.uniforms.intensity_gbc.value[0] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 强度对比度系数 */
  get intensityContrast() {
    return this.uniforms.intensity_gbc.value[2]
  }

  set intensityContrast(value) {
    if (this.uniforms.intensity_gbc.value[2] !== value) {
      this.uniforms.intensity_gbc.value[2] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 强度亮度偏移量 */
  get intensityBrightness() {
    return this.uniforms.intensity_gbc.value[1]
  }

  set intensityBrightness(value) {
    if (this.uniforms.intensity_gbc.value[1] !== value) {
      this.uniforms.intensity_gbc.value[1] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** RGB gamma 校正系数 */
  get rgbGamma() {
    return this.uniforms.uRGB_gbc.value[0]
  }

  set rgbGamma(value) {
    if (this.uniforms.uRGB_gbc.value[0] !== value) {
      this.uniforms.uRGB_gbc.value[0] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** RGB 对比度系数 */
  get rgbContrast() {
    return this.uniforms.uRGB_gbc.value[2]
  }

  set rgbContrast(value) {
    if (this.uniforms.uRGB_gbc.value[2] !== value) {
      this.uniforms.uRGB_gbc.value[2] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** RGB 亮度偏移量 */
  get rgbBrightness() {
    return this.uniforms.uRGB_gbc.value[1]
  }

  set rgbBrightness(value) {
    if (this.uniforms.uRGB_gbc.value[1] !== value) {
      this.uniforms.uRGB_gbc.value[1] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 额外属性 gamma 校正系数 */
  get extraGamma() {
    return this.uniforms.uExtraGammaBrightContr.value[0]
  }

  set extraGamma(value) {
    if (this.uniforms.uExtraGammaBrightContr.value[0] !== value) {
      this.uniforms.uExtraGammaBrightContr.value[0] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 额外属性亮度偏移量 */
  get extraBrightness() {
    return this.uniforms.uExtraGammaBrightContr.value[1]
  }

  set extraBrightness(value) {
    if (this.uniforms.uExtraGammaBrightContr.value[1] !== value) {
      this.uniforms.uExtraGammaBrightContr.value[1] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 额外属性对比度系数 */
  get extraContrast() {
    return this.uniforms.uExtraGammaBrightContr.value[2]
  }

  set extraContrast(value) {
    if (this.uniforms.uExtraGammaBrightContr.value[2] !== value) {
      this.uniforms.uExtraGammaBrightContr.value[2] = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /**
   * 获取指定属性的缓存取值范围。
   *
   * @param attributeName - 属性名
   * @returns 缓存范围 [min, max]；未设置时为 undefined
   */
  getRange(attributeName) {
    return this.ranges.get(attributeName)
  }

  /**
   * 更新指定属性的取值范围，变化时派发 material_property_changed。
   *
   * @param attributeName - 属性名
   * @param newRange - 新范围 [min, max] 或 null
   */
  setRange(attributeName, newRange) {
    let rangeChanged = false

    const oldRange = this.ranges.get(attributeName)

    if (oldRange != null && newRange != null) {
      rangeChanged = oldRange[0] !== newRange[0] || oldRange[1] !== newRange[1]
    } else {
      rangeChanged = true
    }

    this.ranges.set(attributeName, newRange)

    if (rangeChanged) {
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 额外属性归一化范围 [min, max] */
  get extraRange() {
    return this.uniforms.uExtraRange.value
  }

  set extraRange(value) {
    if (!(Array.isArray(value) && value.length === 2)) {
      return
    }

    if (
      value[0] === this.uniforms.uExtraRange.value[0] &&
      value[1] === this.uniforms.uExtraRange.value[1]
    ) {
      return
    }

    this.uniforms.uExtraRange.value = value

    this._defaultExtraRangeChanged = true

    this.dispatchEvent({
      type: 'material_property_changed',
      target: this,
    })
  }

  /** 组合着色中 RGB 权重 */
  get weightRGB() {
    return this.uniforms.wRGB.value
  }

  set weightRGB(value) {
    if (this.uniforms.wRGB.value !== value) {
      this.uniforms.wRGB.value = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 组合着色中强度权重 */
  get weightIntensity() {
    return this.uniforms.wIntensity.value
  }

  set weightIntensity(value) {
    if (this.uniforms.wIntensity.value !== value) {
      this.uniforms.wIntensity.value = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 组合着色中高程权重 */
  get weightElevation() {
    return this.uniforms.wElevation.value
  }

  set weightElevation(value) {
    if (this.uniforms.wElevation.value !== value) {
      this.uniforms.wElevation.value = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 组合着色中分类权重 */
  get weightClassification() {
    return this.uniforms.wClassification.value
  }

  set weightClassification(value) {
    if (this.uniforms.wClassification.value !== value) {
      this.uniforms.wClassification.value = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 组合着色中回波号权重 */
  get weightReturnNumber() {
    return this.uniforms.wReturnNumber.value
  }

  set weightReturnNumber(value) {
    if (this.uniforms.wReturnNumber.value !== value) {
      this.uniforms.wReturnNumber.value = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 组合着色中源 ID 权重 */
  get weightSourceID() {
    return this.uniforms.wSourceID.value
  }

  set weightSourceID(value) {
    if (this.uniforms.wSourceID.value !== value) {
      this.uniforms.wSourceID.value = value
      this.dispatchEvent({
        type: 'material_property_changed',
        target: this,
      })
    }
  }

  /** 是否启用 Z 轴裁切 */
  get enableZClip(): boolean {
    return this.uniforms.uEnableZClip.value
  }

  set enableZClip(value: boolean) {
    this.uniforms.uEnableZClip.value = value
  }

  /** Z 轴裁切范围 [min, max] */
  get zClipRange(): [number, number] {
    return this.uniforms.uZClipRange.value as [number, number]
  }

  set zClipRange(value: [number, number]) {
    this.uniforms.uZClipRange.value = value
  }

  /**
   * 根据渐变色数组生成 canvas 纹理。
   *
   * @param gradient - 渐变数组，[[stop, THREE.Color], ...]
   * @returns 可直接用于 uniform 的 CanvasTexture
   */
  static generateGradientTexture(gradient) {
    const size = 64

    // create canvas
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size

    // get context
    const context = canvas.getContext('2d')!

    // draw gradient
    context.rect(0, 0, size, size)
    const ctxGradient = context.createLinearGradient(0, 0, size, size)

    for (let i = 0; i < gradient.length; i++) {
      const step = gradient[i]

      // Canvas 和点云 Raw Shader 都使用 sRGB 编码值；显式声明目标色彩空间，避免依赖默认值。
      ctxGradient.addColorStop(step[0], `#${step[1].getHexString(THREE.SRGBColorSpace)}`)
    }

    context.fillStyle = ctxGradient
    context.fill()

    //let texture = new THREE.Texture(canvas);
    const texture = new THREE.CanvasTexture(canvas)
    // 自定义 WebGLTexture 上传器不会做颜色空间转换，保持 Canvas 的 sRGB 编码字节。
    texture.colorSpace = THREE.NoColorSpace
    texture.needsUpdate = true

    texture.minFilter = THREE.LinearFilter
    // texture.wrap = THREE.RepeatWrapping;
    // texture.repeat = 2;
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.repeat = new THREE.Vector2(2, 2)
    // textureImage = texture.image;

    return texture
  }

  /**
   * 从完整 URL 加载 matcap 纹理。
   *
   * @param url - matcap 图片 URL
   * @returns 已配置过滤器的 Texture
   */
  static generateMatcapTexture(url: string) {
    const texture = new THREE.TextureLoader().load(url)
    texture.magFilter = texture.minFilter = THREE.LinearFilter
    texture.needsUpdate = true
    return texture
  }

  /** 释放材质持有的纹理资源。 */
  override dispose() {
    this.visibleNodesTexture?.dispose()
    this.gradientTexture?.dispose()
    this.classificationTexture?.dispose()
    this.matcapTexture?.dispose()
    super.dispose()
  }

  /** 暂时禁用事件派发，用于批量更新避免频繁触发监听器。 */
  disableEvents() {
    if (this._hiddenListeners === undefined) {
      this._hiddenListeners = this._listeners
      this._listeners = {}
    }
  }

  /** 恢复被 disableEvents 暂停的事件派发。 */
  enableEvents() {
    this._listeners = this._hiddenListeners
    this._hiddenListeners = undefined
  }

  // copyFrom(from){

  // 	var a = 10;

  // 	for(let name of Object.keys(this.uniforms)){
  // 		this.uniforms[name].value = from.uniforms[name].value;
  // 	}
  // }

  // copy(from){
  // 	this.copyFrom(from);
  // }
}
